import { Stack, Text, Box, Button } from '@ltvco/refresh-lib/theme';
import { LoadingDots } from '@ltvco/refresh-lib/v1';
import { AccessTime } from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';

import { NUMBER_OF_COURT_CASE_DOCKET_ENTRIES_TO_SHOW } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';
import { Message } from 'components/ChatBV/types';
import { PacerRedirectModal } from 'routes/Dashboard/CourtCases/v2/shared';
import { useDocketEntriesData } from 'routes/Dashboard/CourtCases/v2/shared/hooks';
import { Court, CourtDocket } from 'routes/Dashboard/CourtCases/v2/types';
import {
  formatCourtDateWithTime,
  buildPacerDocumentUrl,
} from 'routes/Dashboard/CourtCases/v2/utils';

import { FadedText } from './components';
import { CourtCaseDocketBox } from './CourtCaseDocketBox';
interface CourtCaseAttachmentsProps {
  currentDocketId: string;
  docketData: CourtDocket;
  courtData: Court;
  courtCaseId: string;
}

export const CourtCaseAttachments = ({
  currentDocketId,
  docketData,
  courtData,
  courtCaseId,
}: CourtCaseAttachmentsProps) => {
  const { docketEntries, isLoading } = useDocketEntriesData(currentDocketId);
  const { sendCourtCaseMessage, messages } = useContext(ChatContext);

  const [amountOfResultsToShow, setAmountOfResultsToShow] = useState<number>(
    NUMBER_OF_COURT_CASE_DOCKET_ENTRIES_TO_SHOW
  );
  const [isPacerModalOpen, setIsPacerModalOpen] = useState<boolean>(false);
  const [pacerUrlToOpen, setPacerUrlToOpen] = useState<string>('');

  useEffect(() => {
    const messageAlreadySent = messages.some(
      (message: Message) => message.content === `Court Case: ${docketData?.id}`
    );

    const courCaseData = {
      id: docketData?.id?.toString() || '',
      name: docketData?.case_name,
      docketEntries,
      courtName:
        courtData?.full_name || docketData?.court_id || 'Not Available',
      dateModified: docketData?.date_modified || '',
      assignedTo: docketData?.assigned_to_str || '',
      referredTo: docketData?.referred_to_str || '',
      docketNumber: docketData?.docket_number || '',
      courtId: docketData?.court_id || '',
      dateFiled: docketData?.date_filed || '',
      dateTerminated: docketData?.date_terminated || '',
      dateLastFiling: docketData?.date_last_filing || '',
      cause: docketData?.cause || '',
      natureOfSuit: docketData?.nature_of_suit || '',
      juryDemand: docketData?.jury_demand || '',
      jurisdictionType: docketData?.jurisdiction_type || '',
      courtCaseId,
    };

    if (docketData?.case_name && !messageAlreadySent && !isLoading) {
      sendCourtCaseMessage(docketData?.case_name, courCaseData);
    }
  }, [docketData?.case_name, isLoading]);

  const getCourtDisplayText = () => {
    return courtData?.full_name || docketData?.court_id || 'Not Available';
  };

  const handleBuyOnPacer = (
    pacerDocId: string,
    entryNumber?: number | null
  ) => {
    const url = buildPacerDocumentUrl({
      pacerDocId,
      pacerCaseId: docketData?.pacer_case_id ?? '',
      entryNumber,
      courtId: docketData?.court_id,
      pacerCourtId: courtData?.pacer_court_id,
    });

    if (url) {
      setPacerUrlToOpen(url);
      setIsPacerModalOpen(true);
    }
  };

  const handleContinueToPacer = () => {
    if (pacerUrlToOpen) {
      window.open(pacerUrlToOpen, '_blank', 'noopener,noreferrer');
    }
    setIsPacerModalOpen(false);
  };

  const renderDocketEntries = () => {
    if (isLoading) {
      return <LoadingDots count={3} />;
    }

    if (docketEntries.length === 0) {
      return <Text variant="body1">No docket entries found</Text>;
    }

    return (
      <Stack spacing={2}>
        {docketEntries.slice(0, amountOfResultsToShow).map((entry, index) => (
          <CourtCaseDocketBox
            key={entry.id}
            docketEntry={entry}
            indexString={`# ${index + 1}`}
            handleBuyOnPacer={handleBuyOnPacer}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Stack spacing={2} px={2}>
      <Stack spacing={0.5}>
        <Text variant="h4" sx={{ mb: 0 }}>
          {docketData?.case_name || ''}
        </Text>
        <FadedText variant="body1">{getCourtDisplayText()}</FadedText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 16 }} />
          <FadedText variant="body1">
            Last Updated: {formatCourtDateWithTime(docketData?.date_modified)}
          </FadedText>
        </Box>
      </Stack>
      {renderDocketEntries()}
      {docketEntries.length > amountOfResultsToShow && (
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            setAmountOfResultsToShow(
              amountOfResultsToShow +
                NUMBER_OF_COURT_CASE_DOCKET_ENTRIES_TO_SHOW
            )
          }
        >
          Show More
        </Button>
      )}
      <PacerRedirectModal
        open={isPacerModalOpen}
        onClose={() => setIsPacerModalOpen(false)}
        onContinue={handleContinueToPacer}
      />
    </Stack>
  );
};
